import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  ShieldCheck,
  Send,
  RotateCcw,
  Check,
  Star,
  Sparkles,
} from "lucide-react";
import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import Seo from "@/components/site/Seo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitSponsorInquiry } from "@/lib/api";
import {
  SPONSOR_TIERS,
  SPONSOR_SUPPORTS,
  SPONSOR_BENEFITS,
  SPONSOR_INTEREST_OPTIONS,
  BRAND,
} from "@/lib/constants";

const initial = {
  name: "",
  company: "",
  title: "",
  email: "",
  phone: "",
  website: "",
  interest_level: "",
  notes: "",
};

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Sponsor() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const preselectAndScroll = (label) => {
    setForm((f) => ({ ...f, interest_level: label }));
    scrollToId("sponsor-inquiry");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.interest_level) {
      toast.error("Please choose an interest level.");
      return;
    }
    setSubmitting(true);
    try {
      // Backend payload preserves existing schema — notes carry the optional website
      const payload = {
        ...form,
        notes: form.website
          ? `Website: ${form.website}\n\n${form.notes || ""}`.trim()
          : form.notes,
      };
      delete payload.website;
      await submitSponsorInquiry(payload);
      setSubmitted(true);
      toast.success("Thank you. Your sponsorship inquiry has been received.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo />

      {/* 1. HERO */}
      <PageHero
        pageTitle="Sponsor The Next Stage"
        eyebrowNumber="01"
        eyebrow="Align Your Brand With The Next Stage"
        title="Align Your Brand With"
        highlight="The Next Stage"
        tagline="Sponsor a public speaking competition and showcase built to elevate emerging voices, entrepreneurs, storytellers, leaders, and community builders."
        description="The Next Stage gives selected speakers a professionally produced platform to share meaningful messages before a live audience and judging panel. Sponsors make that platform possible — aligning their brand with communication, courage, entrepreneurship, leadership, and local community growth."
      />

      {/* Hero callout band */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 text-center">
          <p
            data-testid="sponsor-hero-callout"
            className="font-bebas tracking-[0.18em] text-gold-bright text-lg sm:text-xl uppercase"
          >
            Major sponsorship opportunities begin at $5,000, with one exclusive
            Presenting Sponsor opportunity available at $15,000 per show.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <button
              data-testid="sponsor-hero-primary-cta"
              onClick={() => scrollToId("sponsor-inquiry")}
              className="btn-gold"
            >
              Inquire About Sponsorship
              <ArrowRight size={16} />
            </button>
            <button
              data-testid="sponsor-hero-secondary-cta"
              onClick={() => scrollToId("sponsorship-levels")}
              className="btn-outline"
            >
              View Sponsorship Levels
            </button>
          </div>
        </div>
      </section>

      {/* 2. WHY SPONSOR */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel number="02">Why Sponsor</SectionLabel>
            <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
              Brand visibility. Community alignment.
              <br />
              <span className="gold-text">Content that carries beyond the event.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="font-manrope text-soft-gray text-lg leading-relaxed">
              The Next Stage is not a generic networking mixer, open mic, or
              pay-to-play speaking showcase. It is a professionally produced
              competition and showcase where selected speakers earn their place
              through message, readiness, authenticity, audience value, and
              event fit.
            </p>
            <p className="font-manrope text-soft-gray text-lg leading-relaxed mt-4">
              Sponsors align with a platform designed to support
              entrepreneurship, communication, leadership, personal growth,
              storytelling, and community connection. A logo on a banner can be
              forgotten. A brand associated with someone stepping onto a stage
              and changing the trajectory of their life is remembered.
            </p>
            <p className="font-manrope text-soft-gray text-lg leading-relaxed mt-4">
              Sponsors do not merely fund an event.
            </p>
            <p className="font-bebas tracking-[0.12em] text-gold-bright text-2xl mt-3">
              They build the stage.
            </p>

            <div
              data-testid="sponsor-why-callout"
              className="corner-bracket card-dark p-7 mt-8"
            >
              <p className="font-cormorant italic text-2xl lg:text-3xl text-gold-bright leading-snug">
                &ldquo;The stage is earned by the message, not purchased by the
                speaker.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHAT SPONSORSHIP SUPPORTS */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="03">What Sponsorship Supports</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">
            Where the dollars <span className="gold-text">actually go.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-4 max-w-3xl leading-relaxed">
            Sponsor support covers essential event needs, including:
          </p>
          <div
            data-testid="sponsor-supports-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10"
          >
            {SPONSOR_SUPPORTS.map((s, i) => (
              <div key={s} className="card-dark p-6">
                <div className="font-shoulders font-black text-3xl text-gold-deep">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="font-manrope text-soft-gray mt-2">{s}</p>
              </div>
            ))}
          </div>
          <p className="font-manrope text-muted-ink mt-8 max-w-3xl text-sm leading-relaxed">
            This support keeps the event professional, accessible,
            mission-driven, and sustainable without placing the financial burden
            on contestants.
          </p>
        </div>
      </section>

      {/* 4. SPONSORSHIP LEVELS */}
      <section
        id="sponsorship-levels"
        className="bg-navy border-t border-slate-soft scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="04">Sponsorship Levels</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">
            Built for <span className="gold-text">Serious Brand Alignment.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-4 max-w-3xl leading-relaxed">
            The Next Stage sponsorship model is designed for organizations that
            want meaningful visibility, community alignment, and association
            with entrepreneurship, communication, leadership, and emerging
            public voices. Sponsorship is far more than logo placement — it
            signals active engagement in the process and committed support for
            the entire business and entrepreneurial community.
          </p>
          <p className="font-bebas tracking-[0.18em] text-gold-bright text-lg mt-3 uppercase">
            They build the stage.
          </p>

          <div
            data-testid="sponsor-tiers-grid"
            className="mt-10 grid md:grid-cols-3 gap-6"
          >
            {SPONSOR_TIERS.map((t) => {
              const slug = t.name.toLowerCase().replace(/\s+/g, "-");
              return (
                <div
                  key={t.name}
                  data-testid={`sponsor-tier-${slug}`}
                  className={`card-dark p-8 flex flex-col relative ${
                    t.feature ? "corner-bracket border-gold" : ""
                  }`}
                >
                  {t.feature && (
                    <div className="absolute -top-3 left-6 bg-gold text-midnight px-3 py-1 font-bebas tracking-[0.22em] text-xs uppercase flex items-center gap-1.5">
                      <Star size={12} fill="currentColor" />
                      Lead Underwriter
                    </div>
                  )}
                  <h3 className="font-shoulders font-black uppercase tracking-wider text-2xl text-white mt-2">
                    {t.name}
                  </h3>
                  <div className="font-bebas text-gold text-4xl mt-2 leading-none">
                    {t.price}
                  </div>
                  {t.limit && (
                    <div
                      data-testid={`sponsor-tier-limit-${slug}`}
                      className="font-bebas tracking-[0.18em] text-gold-bright text-xs uppercase mt-1"
                    >
                      {t.limit}
                    </div>
                  )}
                  <div className="gold-line" />
                  <p className="font-manrope text-soft-gray mt-4 flex-1 leading-relaxed text-[15px]">
                    {t.blurb}
                  </p>
                  <button
                    type="button"
                    data-testid={`sponsor-tier-cta-${slug}`}
                    onClick={() => preselectAndScroll(t.formValue || t.name)}
                    className="font-bebas tracking-[0.22em] text-gold hover:text-gold-bright mt-6 inline-flex items-center gap-2 text-sm self-start"
                  >
                    {t.cta || "Inquire"} <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Additional partnership opportunities */}
          <div
            data-testid="sponsor-partnerships-block"
            className="card-dark p-8 mt-10 grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center"
          >
            <Sparkles className="text-gold shrink-0" size={32} />
            <div>
              <div className="font-bebas tracking-[0.22em] text-gold text-sm uppercase">
                Additional Alignment Opportunities
              </div>
              <p className="font-manrope text-soft-gray mt-2 leading-relaxed">
                Limited in-kind, media, hospitality, award, vendor, community
                partner, and strategic partnership opportunities may be
                available by inquiry. These are evaluated based on event needs,
                sponsor fit, audience value, and alignment with the mission of
                The Next Stage.
              </p>
            </div>
            <button
              type="button"
              data-testid="sponsor-partnership-cta"
              onClick={() => preselectAndScroll("In-Kind Sponsorship")}
              className="btn-outline shrink-0"
            >
              Discuss Partnership Options
            </button>
          </div>

          <p className="font-manrope text-muted-ink mt-8 max-w-3xl text-sm leading-relaxed">
            Sponsorship benefits vary by level and may include website
            recognition, event signage, stage acknowledgment, program
            placement, sponsor seating, VIP access, media recap inclusion,
            social media recognition, award alignment, and post-event sponsor
            appreciation. Final sponsor benefits and deliverables are confirmed
            in writing before activation.
          </p>
        </div>
      </section>

      {/* 5. SPONSOR BENEFITS OVERVIEW */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="05">Sponsor Benefits May Include</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] max-w-3xl">
            Visibility, alignment, goodwill,{" "}
            <span className="gold-text">engagement.</span>
          </h2>
          <ul
            data-testid="sponsor-benefits-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 mt-10"
          >
            {SPONSOR_BENEFITS.map((b) => (
              <li
                key={b}
                className="flex items-start gap-3 font-manrope text-soft-gray"
              >
                <Check
                  size={18}
                  strokeWidth={2.4}
                  className="text-gold-bright mt-1 shrink-0"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <p className="font-manrope text-muted-ink mt-10 max-w-3xl text-sm leading-relaxed">
            Sponsor benefits are designed to create visibility, alignment,
            goodwill, and engagement. The Next Stage does not guarantee
            attendance numbers, direct sales, leads, media coverage, or business
            outcomes.
          </p>
        </div>
      </section>

      {/* 6. FOUNDING SPONSOR OPPORTUNITY */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="06">Founding Sponsor Opportunity</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
            Launch the platform{" "}
            <span className="gold-text">before the room gets big.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-6 leading-relaxed max-w-3xl">
            Sponsors who back the inaugural pilot series earn recognition as
            founding supporters of The Next Stage — a new platform for emerging
            speakers, entrepreneurs, storytellers, authors, advocates, and
            community leaders launching in Colorado Springs.
          </p>
          <p className="font-manrope text-soft-gray mt-4 leading-relaxed max-w-3xl">
            Founding Sponsor recognition is available for qualifying sponsors
            who commit at select levels before the first confirmed event. These
            early sponsors are recognized as part of the foundation that brought
            the platform to life.
          </p>

          <div
            data-testid="sponsor-founding-callout"
            className="corner-bracket card-dark p-7 mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <p className="font-cormorant italic text-2xl lg:text-3xl text-gold-bright leading-snug flex-1">
              Founding sponsors launch the platform before the room gets big.
            </p>
            <button
              type="button"
              data-testid="sponsor-founding-cta"
              onClick={() => preselectAndScroll("Not Sure Yet \u2014 Let\u2019s Discuss")}
              className="btn-gold shrink-0"
            >
              Ask About Founding Sponsor Recognition
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* 6.5 NATIONAL EXPANSION CONTEXT */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="07">A National Series</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
            One Platform.
            <br />
            <span className="gold-text">Multiple Cities.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-6 leading-relaxed max-w-3xl">
            The Colorado Springs pilot is the first show of an ongoing series.
            The Next Stage Media Group, LLC is actively planning additional
            Next Stage events in markets across the country.
          </p>

          <div
            data-testid="sponsor-expansion-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10"
          >
            <div className="card-dark p-6 corner-bracket border-gold">
              <div className="font-bebas tracking-[0.22em] text-gold-bright text-xs uppercase">
                Pilot
              </div>
              <div className="font-bebas text-white text-2xl leading-tight mt-2">
                Colorado Springs
              </div>
              <p className="font-manrope text-soft-gray text-sm mt-3 leading-relaxed">
                The inaugural show. Founding sponsors are recognized here first.
              </p>
            </div>
            {BRAND.expansionMarkets.map((m) => (
              <div
                key={m}
                data-testid={`sponsor-expansion-${m.toLowerCase().replace(/\s+/g, "-")}`}
                className="card-dark p-6"
              >
                <div className="font-bebas tracking-[0.22em] text-gold text-xs uppercase">
                  Planned
                </div>
                <div className="font-bebas text-white text-2xl leading-tight mt-2">
                  {m}
                </div>
                <p className="font-manrope text-soft-gray text-sm mt-3 leading-relaxed">
                  Future Next Stage event.
                </p>
              </div>
            ))}
          </div>

          <p className="font-manrope text-soft-gray mt-10 leading-relaxed max-w-3xl">
            A sponsorship of the Colorado Springs pilot is more than a single
            event commitment. It is alignment with a national platform built to
            elevate emerging speakers across multiple cities. Sponsors who join
            the platform early gain first consideration for renewed alignment in
            future markets as the series expands.
          </p>
          <p className="font-bebas tracking-[0.18em] text-gold-bright text-lg mt-4 uppercase">
            Sponsor one show. Build alignment with the platform.
          </p>
        </div>
      </section>

      {/* 7. ACTIVATION POLICY */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div
            data-testid="sponsor-activation-block"
            className="corner-bracket card-dark p-10"
          >
            <div className="flex items-start gap-5">
              <ShieldCheck className="text-gold shrink-0" size={32} />
              <div>
                <div className="label-bebas text-gold">Activation Policy</div>
                <h3 className="font-bebas text-white text-3xl sm:text-4xl leading-tight mt-2">
                  Funds Activate When The Event Is Confirmed.
                </h3>
                <p className="font-manrope text-soft-gray mt-4 leading-relaxed">
                  Sponsorship commitments activate once minimum operating
                  requirements are met and the event is publicly confirmed.
                </p>
                <p className="font-manrope text-soft-gray mt-3 leading-relaxed">
                  If a pilot event does not move forward, sponsors may choose
                  either a refund or a roll-forward commitment to a future
                  confirmed event.
                </p>
                <p className="font-manrope text-soft-gray mt-3 leading-relaxed">
                  We treat sponsorship as a partnership, not a transaction.
                  Sponsor commitments, benefits, payment terms, event details,
                  and recognition obligations should be confirmed in writing
                  before public sponsor announcements are made.
                </p>
                <p
                  data-testid="sponsor-tax-note"
                  className="font-manrope text-muted-ink text-sm mt-5 leading-relaxed border-t border-slate-soft pt-4"
                >
                  <span className="text-gold">Important Note:</span> The Next
                  Stage is produced by {BRAND.producer}, a for-profit company.
                  Sponsorship commitments are business sponsorships and are not
                  tax-deductible charitable contributions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. SPONSOR INTEGRITY & EVENT CONTROL */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="08">Sponsor Integrity & Event Control</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95]">
            Sponsors support the platform.
            <br />
            <span className="gold-text">They do not buy the outcome.</span>
          </h2>
          <p className="font-manrope text-soft-gray mt-6 leading-relaxed max-w-3xl">
            The Next Stage is built around speaker integrity, audience trust,
            and fair competition. Sponsorship does not create ownership,
            control, management authority, judging influence, contestant
            selection authority, or programming rights unless specifically
            agreed in writing.
          </p>
          <p className="font-manrope text-soft-gray mt-4 leading-relaxed max-w-3xl">
            Sponsors may receive recognition, visibility, and approved
            engagement opportunities based on their sponsorship level. Sponsors
            may not influence judging outcomes, contestant selection, award
            decisions, or speaker content.
          </p>
          <p className="font-manrope text-soft-gray mt-4 leading-relaxed max-w-3xl">
            The producer reserves the right to accept or decline sponsorships
            based on brand fit, event values, reputational concerns, legal
            concerns, or potential conflicts with the purpose of the event.
          </p>

          <div
            data-testid="sponsor-integrity-callout"
            className="corner-bracket card-dark p-7 mt-8 max-w-3xl"
          >
            <p className="font-cormorant italic text-2xl lg:text-3xl text-gold-bright leading-snug">
              Sponsors support the platform. They do not buy the outcome.
            </p>
          </div>
        </div>
      </section>

      {/* 9. SPONSORSHIP INQUIRY FORM */}
      <section
        id="sponsor-inquiry"
        className="bg-midnight border-t border-slate-soft scroll-mt-24"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="09" align="center">
            Sponsorship Inquiry
          </SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl leading-[0.95] text-center">
            Let&rsquo;s find the right{" "}
            <span className="gold-text">alignment</span> for your organization.
          </h2>
          {submitted ? (
            <div
              data-testid="sponsor-thank-you"
              className="corner-bracket card-dark p-10 mt-8 text-center"
            >
              <div className="label-bebas text-gold">Received</div>
              <h3 className="font-bebas text-white text-3xl mt-2">
                Thank You For Your Inquiry.
              </h3>
              <p className="font-manrope text-soft-gray mt-4 max-w-md mx-auto leading-relaxed">
                Thank you for your interest in sponsoring The Next Stage.
                We&rsquo;ll review your inquiry and follow up to discuss
                alignment, availability, and next steps.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="sponsor-submit-another"
                className="btn-outline mt-8"
              >
                <RotateCcw size={18} strokeWidth={1.8} />
                Submit Another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="sponsor-inquiry-form"
              className="card-dark p-8 sm:p-10 mt-8 space-y-6"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-bebas" htmlFor="sp-name">
                    Full Name *
                  </label>
                  <input
                    id="sp-name"
                    name="name"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid="sponsor-input-name"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="sp-company">
                    Company / Organization *
                  </label>
                  <input
                    id="sp-company"
                    name="company"
                    required
                    value={form.company}
                    onChange={onChange}
                    data-testid="sponsor-input-company"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="sp-title">
                    Title / Role
                  </label>
                  <input
                    id="sp-title"
                    name="title"
                    value={form.title}
                    onChange={onChange}
                    data-testid="sponsor-input-title"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="sp-email">
                    Email *
                  </label>
                  <input
                    id="sp-email"
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="sponsor-input-email"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="sp-phone">
                    Phone
                  </label>
                  <input
                    id="sp-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="sponsor-input-phone"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="label-bebas" htmlFor="sp-website">
                    Website
                  </label>
                  <input
                    id="sp-website"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    data-testid="sponsor-input-website"
                    className="input-dark"
                    placeholder="https://"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-bebas">Interest Level *</label>
                  <Select
                    value={form.interest_level}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, interest_level: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid="sponsor-select-interest"
                      className="input-dark rounded-none h-[52px] text-left"
                    >
                      <SelectValue placeholder="Choose an interest level" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-card border-slate-soft text-white">
                      {SPONSOR_INTEREST_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="label-bebas" htmlFor="sp-notes">
                  Custom Alignment Notes
                </label>
                <textarea
                  id="sp-notes"
                  name="notes"
                  rows={4}
                  value={form.notes}
                  onChange={onChange}
                  data-testid="sponsor-input-notes"
                  className="input-dark"
                  placeholder="Tell us about your brand, your audience, and how you would like to show up as part of The Next Stage."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="sponsor-submit-button"
                className="btn-gold w-full sm:w-auto"
              >
                <Send size={18} strokeWidth={1.8} />
                {submitting ? "Sending\u2026" : "Submit Sponsorship Inquiry"}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
